import React, { useMemo } from "react";
import { T, fmt } from "./theme";
import { SectionHeader, Card, Progress, Note } from "./ui";
import { Icon } from "./Icons";
import { calcPorRegional, statusMetaColetiva } from "./awards";

// ═══════════════════════════════════════════════════════════════════════
// REGIONAL — meta mensal por UF (Caminho A: Volume + Ativações)
// ═══════════════════════════════════════════════════════════════════════

const UF_NOMES = {
  CE: "Ceará", SP: "São Paulo", PB: "Paraíba", SE: "Sergipe",
  AM: "Amazonas", RJ: "Rio de Janeiro", MG: "Minas Gerais",
  BA: "Bahia", PE: "Pernambuco", RN: "Rio Grande do Norte",
};

export default function Regional({ corretores, metas }) {
  const regs = useMemo(
    () => calcPorRegional(corretores).sort((a, b) => b.valor - a.valor),
    [corretores]
  );
  const m = metas.regional;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <SectionHeader
        title="Regional"
        subtitle={`Ciclo mensal · ${fmt.brl(m.metaVolume)} em volume E ${m.metaAtivacoes} ativações por regional`}
        icon={<Icon.map size={24} />}
      />

      <div style={{ display: "grid", gap: 10 }}>
        {m.premioVolume && <Note tone="brand" icon={<Icon.gift size={14} />}><b>Meta Volume:</b> {m.premioVolume}</Note>}
        {m.premioAtivacoes && <Note tone="info" icon={<Icon.users size={14} />}><b>Meta Ativações:</b> {m.premioAtivacoes}</Note>}
        {m.premioSupermeta && <Note tone="ok" icon={<Icon.rocket size={14} />}><b>🏆 Supermeta:</b> {m.premioSupermeta}</Note>}
      </div>

      <div style={{ display: "grid", gap: 14 }}>
        {regs.map((r, i) => (
          <RegionalCard key={r.uf} regional={r} posicao={i + 1} metas={m} />
        ))}
      </div>

      {regs.length === 0 && (
        <Card>
          <div style={{ textAlign: "center", padding: 40, color: T.textDim, fontSize: 13 }}>
            Nenhuma regional ativa no momento.
          </div>
        </Card>
      )}
    </div>
  );
}

function RegionalCard({ regional: r, posicao, metas }) {
  const st = statusMetaColetiva({
    atingidoVol: r.valor, atingidoAtiv: r.ativados,
    metaVol: metas.metaVolume, metaAtiv: metas.metaAtivacoes,
  });

  const statusInfo = {
    supermeta:  { label: "Supermeta 🏆",    color: T.ok,     bg: T.okSoft,    icon: "🏆" },
    volume:     { label: "Meta volume",     color: T.brand,  bg: T.brandSoft, icon: "💰" },
    ativacoes:  { label: "Meta ativações",  color: T.info,   bg: T.infoSoft,  icon: "👥" },
    parcial:    { label: "Em progresso",    color: T.textSub, bg: T.cardSoft,  icon: "○" },
  }[st];

  const pctVol = Math.min((r.valor / metas.metaVolume) * 100, 100);
  const pctAtiv = Math.min((r.ativados / metas.metaAtivacoes) * 100, 100);

  return (
    <div style={{
      background: statusInfo.bg,
      border: `1px solid ${statusInfo.color}${st === "parcial" ? "" : "44"}`,
      borderRadius: T.r.lg, padding: 22,
      borderLeft: `4px solid ${statusInfo.color}`,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div className="serif" style={{
              width: 36, height: 36, borderRadius: "50%",
              background: posicao === 1 ? T.brand : T.card,
              color: posicao === 1 ? T.textInv : T.textSub,
              border: posicao === 1 ? "none" : `1px solid ${T.border}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 16, fontWeight: 700,
            }}>{posicao}</div>
            <div>
              <div className="serif" style={{ fontSize: 22, fontWeight: 600, color: T.brand, letterSpacing: -0.3 }}>
                {UF_NOMES[r.uf] || r.uf} · {r.uf}
              </div>
              <div style={{ fontSize: 11, color: T.textSub, marginTop: 2 }}>
                {r.cidades.join(" · ")} · {r.membros.length} {r.membros.length === 1 ? "corretor" : "corretores"}
              </div>
            </div>
          </div>
        </div>

        <div style={{ textAlign: "right" }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: statusInfo.color,
            padding: "4px 12px", borderRadius: 100,
            background: "rgba(255,255,255,0.6)",
            border: `1px solid ${statusInfo.color}44`,
            letterSpacing: 0.5, textTransform: "uppercase",
            display: "inline-flex", alignItems: "center", gap: 6,
          }}>
            {statusInfo.icon} {statusInfo.label}
          </div>
        </div>
      </div>

      {/* 2 metas lado a lado */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
            <div style={{ fontSize: 10, color: T.textSub, letterSpacing: 0.5, textTransform: "uppercase", fontWeight: 700 }}>
              💰 Volume (R$)
            </div>
            <div style={{ fontSize: 10, color: pctVol >= 100 ? T.ok : T.textSub, fontWeight: 700 }}>
              {pctVol >= 100 ? "✓" : `${pctVol.toFixed(0)}%`}
            </div>
          </div>
          <div className="serif" style={{ fontSize: 20, fontWeight: 600, color: T.text, lineHeight: 1, marginBottom: 6 }}>
            {fmt.brl(r.valor)}
          </div>
          <Progress value={r.valor} max={metas.metaVolume} color={T.brand} height={5} />
          <div style={{ fontSize: 10, color: T.textDim, marginTop: 4 }}>
            Meta: {fmt.brl(metas.metaVolume)}
          </div>
        </div>

        <div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
            <div style={{ fontSize: 10, color: T.textSub, letterSpacing: 0.5, textTransform: "uppercase", fontWeight: 700 }}>
              👥 Ativações
            </div>
            <div style={{ fontSize: 10, color: pctAtiv >= 100 ? T.ok : T.textSub, fontWeight: 700 }}>
              {pctAtiv >= 100 ? "✓" : `${pctAtiv.toFixed(0)}%`}
            </div>
          </div>
          <div className="serif" style={{ fontSize: 20, fontWeight: 600, color: T.text, lineHeight: 1, marginBottom: 6 }}>
            {r.ativados} <span style={{ fontSize: 12, color: T.textSub }}>vivas</span>
          </div>
          <Progress value={r.ativados} max={metas.metaAtivacoes} color={T.info} height={5} />
          <div style={{ fontSize: 10, color: T.textDim, marginTop: 4 }}>
            Meta: {metas.metaAtivacoes} ativações
          </div>
        </div>
      </div>

      {/* Stats extras */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginTop: 16 }}>
        <Stat label="Pontos" value={r.pts} />
        <Stat label="Vendas" value={r.vendas} />
        <Stat label="Cadastros" value={r.cadastrados} />
        <Stat label="Cidades" value={r.cidades.length} />
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: T.r.md, padding: "8px 12px" }}>
      <div style={{ fontSize: 9, color: T.textDim, letterSpacing: 1, textTransform: "uppercase", fontWeight: 600 }}>{label}</div>
      <div className="serif" style={{ fontSize: 18, fontWeight: 600, color: T.text, marginTop: 2 }}>{value}</div>
    </div>
  );
}
